import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';

import { StudentTokenStorage } from '../auth/student-token.storage';
import { TokenStorage } from '../auth/token.storage';
import { SKIP_AUTH } from './public-request.context';

/**
 * Adds `Authorization: Bearer <jwt>` to outgoing requests when a token is
 * stored. Skips the login endpoints (which would otherwise carry a stale
 * token) and any URL marked with the `X-Public-Endpoint` header (set and
 * stripped by the request issuer when intentionally bypassing auth).
 *
 * Token-stack routing (ADR-0036): `/student-portal/` URLs authenticate
 * with the STUDENT token; everything else uses the staff token. Before
 * 2026-06-11 this interceptor blindly attached the staff token to every
 * request, which (a) clobbered manually-set student Authorization headers
 * when both sessions coexisted in one browser, and (b) left student
 * homework calls (which set no manual header) with either no auth or the
 * wrong stack's token. Found by browser-walking /student/homework with a
 * staff session also active: the upcoming-homework call 401'd.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  // Explicitly anonymous requests (e.g. the public registration flow) must
  // never carry a token, even if a parent/student left a portal open in
  // another tab and their token is in shared localStorage. Checked first so
  // no later branch can attach one.
  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  // Login endpoints never carry a Bearer token.
  if (
    req.url.endsWith('/auth/token/') ||
    req.url.endsWith('/auth/token') ||
    req.url.endsWith('/student-portal/auth/login/')
  ) {
    return next(req);
  }

  // Explicit opt-out (rare).
  if (req.headers.has('X-Public-Endpoint')) {
    return next(req.clone({ headers: req.headers.delete('X-Public-Endpoint') }));
  }

  // Respect an Authorization header the caller set explicitly (e.g.
  // student-api.service.ts builds its own). Never overwrite it.
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  // A few /student-portal/ endpoints are staff/parent-facing account
  // management (a manager enables an account; a parent requests or resets a
  // child's). They authenticate with the MAIN token stack despite the URL
  // prefix, so they must NOT be routed to the student token — otherwise a
  // staff/parent caller (who holds no student token) sends the request
  // unauthenticated and Django's session-auth fallback rejects the POST with
  // a CSRF error.
  const isStaffStudentPortalPath =
    req.url.includes('/student-portal/admin/') ||
    req.url.includes('/student-portal/request-account/') ||
    req.url.includes('/student-portal/reset-child-password/');

  // Student-portal endpoints → student token stack (except the staff/parent
  // account-management endpoints above).
  if (req.url.includes('/student-portal/') && !isStaffStudentPortalPath) {
    const studentToken = StudentTokenStorage.get();
    if (!studentToken) return next(req);
    return next(req.clone({ setHeaders: { Authorization: `Bearer ${studentToken}` } }));
  }

  const token = TokenStorage.get();
  if (!token) return next(req);

  return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
};
